import React from 'react';
import _ from 'lodash';

import {
  BaseWidget,
  selectFields
} from 'modules/layer01';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Stack
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  DeleteForever
} from '@mui/icons-material';
import {
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';

import { appFormUtil } from 'modules/layer00';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const { genController } = selectFields.OrderPropertySelectField.genComponentAndControllerGenerator();
const ControlledOrderPropertySelectField = genController<types.IFormState>();



export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.storeId);
  const [saveAsnFormat, saveAsnFormatTuple] = dataService.useSaveAsnFormat(props.storeId);
  const form = useForm<types.IFormState>({});
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'properties'
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['properties']);



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      const { properties } = appFormUtil.trimFormStateValues(formState);
      if (_.some(properties, prop => _.isNil(prop.orderProperty))) {
        alert("You must specify an order property");
        return;
      }

      saveAsnFormat({ properties });
    },
    [saveAsnFormat]
  );



  const handleClickAddColumn = React.useCallback(
    () => {
      fieldArray.append({
        customColumnHeader: '',
        defaultValue: '',
        orderProperty: null
      });
    },
    [fieldArray]
  );

  const handleClickDeleteColumn = React.useCallback(
    (index: number) => {
      fieldArray.remove(index);
    },
    [fieldArray]
  );

  const handleClickDown = React.useCallback(
    (index: number) => {
      fieldArray.swap(index, index + 1);
    },
    [fieldArray]
  );

  const handleClickUp = React.useCallback(
    (index: number) => {
      fieldArray.swap(index, index - 1);
    },
    [fieldArray]
  );



  const loading = queryTuple.loading || saveAsnFormatTuple.loading;

  return (
    <Form.FC
      disableDefaultOverflow
      onSubmit={form.handleSubmit(processSubmit)}>
      <BaseWidget.FC
        title="Custom ASN Format"
        elAction={(
          <Stack
            direction="row"
            spacing={2}>
            <Button
              disabled={loading}
              onClick={handleClickAddColumn}>
              Add Column
            </Button>

            <Button
              disabled={loading}
              type="submit"
              variant="contained">
              Save
            </Button>
          </Stack>
        )}>

        <Grid
          container
          rowSpacing={1}
          columnSpacing={1}>
          {fieldArray.fields.map((field, index) => (
            <React.Fragment key={field.id}>
              <Grid item xs={2}>
                <IconButton
                  disabled={index === fieldArray.fields.length - 1}
                  onClick={_.partial(handleClickDown, index)}>
                  <ArrowDownward />
                </IconButton>

                <IconButton
                  disabled={index === 0}
                  onClick={_.partial(handleClickUp, index)}>
                  <ArrowUpward />
                </IconButton>
              </Grid>

              <Grid
                item
                xs={4}>
                <ControlledOrderPropertySelectField
                  disableDefaultMargins
                  isRequired
                  isDisabled={loading}
                  label="Property"
                  formControl={form.control}
                  fieldKey={`properties.${index}.orderProperty`} />
              </Grid>

              <Grid
                item
                xs={2}>
                <ControlledTextField
                  disableDefaultMargins
                  isRequired
                  isDisabled={loading}
                  label="Custom Column Header"
                  formControl={form.control}
                  fieldKey={`properties.${index}.customColumnHeader`} />
              </Grid>

              <Grid
                item
                xs={2}>
                <ControlledTextField
                  disableDefaultMargins
                  isDisabled={loading}
                  label="Default Value"
                  formControl={form.control}
                  fieldKey={`properties.${index}.defaultValue`} />
              </Grid>

              <Grid
                item
                xs={2}>
                <IconButton onClick={_.partial(handleClickDeleteColumn, index)}>
                  <DeleteForever />
                </IconButton>
              </Grid>

              {index < fieldArray.fields.length - 1 && (
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>

      </BaseWidget.FC>
    </Form.FC>
  );
};
